import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import Skeleton from 'components/skeleton/Skeleton';
import { formatLocalTime } from 'utils/formatDate';
import styles from './casinoHistory.module.scss';

interface CasinoHistoryTableProps {
  data: any[];
  loading: boolean;
  limit: number;
}

const CasinoHistoryTable: React.FC<CasinoHistoryTableProps> = ({ data, loading, limit }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.responsiveScroll}>
      <div className={styles.tableMinWidth}>
        <div className={styles.thead}>
          <div className={styles.tr}>
            <span>{t('casinoHistory.columns.game', 'Game')}</span>
            <span>{t('casinoHistory.columns.betAmount', 'Bet amount')}</span>
            <span>{t('casinoHistory.columns.multiplier', 'Multiplier')}</span>
            <span>{t('casinoHistory.columns.payout', 'Payout')}</span>
            <span>{t('casinoHistory.columns.date', 'Date')}</span>
          </div>
        </div>

        <div className={styles.tbody}>
          {loading ? (
            Array.from({ length: limit }).map((_, idx) => (
              <div key={idx} className={styles.trBody}>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('casinoHistory.columns.game', 'Game')}</span>
                  <Skeleton style={{ width: '32px', height: '32px', borderRadius: '6px' }} />
                  <Skeleton
                    style={{
                      width: '80px',
                      height: '14px',
                      borderRadius: '4px',
                      marginLeft: '8px',
                    }}
                  />
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('casinoHistory.columns.betAmount', 'Bet amount')}</span>
                  <Skeleton style={{ width: '50px', height: '14px', borderRadius: '4px' }} />
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('casinoHistory.columns.multiplier', 'Multiplier')}</span>
                  <Skeleton style={{ width: '40px', height: '14px', borderRadius: '4px' }} />
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('casinoHistory.columns.payout', 'Payout')}</span>
                  <Skeleton style={{ width: '60px', height: '14px', borderRadius: '4px' }} />
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('casinoHistory.columns.date', 'Date')}</span>
                  <Skeleton style={{ width: '60px', height: '14px', borderRadius: '4px' }} />
                </div>
              </div>
            ))
          ) : data.length === 0 ? (
            <div className={styles.emptyState}>{t('casinoHistory.emptyState', 'No casino history found')}</div>
          ) : (
            data.map((item: any, idx: number) => {
              const isWin = parseFloat(item.payoutAmount || '0') > 0;
              return (
                <div key={idx} className={styles.trBody}>
                  <div className={styles.td}>
                    <span className={styles.mobileLabel}>{t('casinoHistory.columns.game', 'Game')}</span>
                    <div className={styles.gameInfo}>
                      <img src={item.imageUrl || ''} alt="" className={styles.gameThumb} />
                      <span className={styles.gameName}>
                        {item.gameTitle || t('casinoHistory.unknownGame', 'Unknown Game')}
                      </span>
                    </div>
                  </div>
                  <div className={styles.td}>
                    <span className={styles.mobileLabel}>{t('casinoHistory.columns.betAmount', 'Bet amount')}</span>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                      parseFloat(item.betAmount || '0'),
                    )}
                  </div>
                  <div className={styles.td}>
                    <span className={styles.mobileLabel}>{t('casinoHistory.columns.multiplier', 'Multiplier')}</span>
                    {item.multiplier || '0x'}
                  </div>
                  <div className={cx(styles.td, { [styles.win]: isWin, [styles.loss]: !isWin })}>
                    <span className={styles.mobileLabel}>{t('casinoHistory.columns.payout', 'Payout')}</span>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                      parseFloat(item.payoutAmount || '0'),
                    )}
                  </div>
                  <div className={styles.td}>
                    <span className={styles.mobileLabel}>{t('casinoHistory.columns.date', 'Date')}</span>
                    {item.date ? formatLocalTime(item.date) : '-'}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default CasinoHistoryTable;
